import { produce } from "immer";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import LoadingStepSkeleton from "@libs/acp/components/LoadingStepSkeleton";

import type { Flows } from "@libs/acp/flows/types";
import type { SchemaStepsComponents, UseFlowStore } from "./types";

import { flowInitialState } from "./initialState";

const filterActiveStepsComponents = (
	activeFlows: Flows[],
	schemaStepsComponents: SchemaStepsComponents,
) => {
	const filteredStepsComponents = Object.entries(schemaStepsComponents).reduce(
		(acc, [key, value]) => {
			const { useInFlow } = value;

			let hasActiveFlowsLogicMatch = activeFlows.includes(useInFlow as Flows);

			if (Array.isArray(useInFlow)) {
				hasActiveFlowsLogicMatch = useInFlow.some((flow) =>
					activeFlows.includes(flow),
				);
			}

			if (hasActiveFlowsLogicMatch) {
				acc[key] = value;
			}

			return acc;
		},
		{} as SchemaStepsComponents,
	);

	return filteredStepsComponents;
};

export const useFlowStore = create<UseFlowStore>()(
	devtools(
		persist(
			(set, get) => {
				const setState = (fn: (state: UseFlowStore) => void) =>
					set(produce(fn));

				return {
					currentFlow: flowInitialState,
					formState: {} as FormData,
					documentResponse: { document: "" },
					checkEmailResponse: {
						require_code_check: false,
						email: "",
						isLoading: false,
					},
					kbaData: {
						missingAttempts: 0,
						stepQuestionData: {},
					},
					flowResponse: [""],
					initializeForm: (
						schemaStepsComponents,
						flow,
						firstStepName,
						lastStepName,
						stepsName,
						currentSchemaFormValidation,
						schemasFormValidations,
						formType,
					) => {
						setState((state) => {
							state.currentFlow = {
								currentStep: firstStepName,
								lastVisitedStep: firstStepName,
								firstStepName: firstStepName,
								lastStepName: lastStepName,
								stepsName: stepsName,
								flowId: get().currentFlow.flowId || "",
								CurrentStepComponent:
									schemaStepsComponents[firstStepName]?.component ??
									LoadingStepSkeleton,
								schemaStepsComponents: schemaStepsComponents,
								activeSchemaStepsComponents: filterActiveStepsComponents(
									[flow],
									schemaStepsComponents,
								),
								activeFlows: [flow],
								currentSchemaFormValidation: currentSchemaFormValidation,
								schemasFormValidations: schemasFormValidations,
								isLoading: false,
								isValid: false,
								formType: formType,
							};
						});
					},
					addFlow: (flow) => {
						const newFlows = [...get().currentFlow.activeFlows, flow];
						const schemaStepsComponents =
							get().currentFlow.schemaStepsComponents;
						const newActiveSchemaStepsComponents = filterActiveStepsComponents(
							newFlows,
							schemaStepsComponents,
						);
						const newStepsName = Object.keys(newActiveSchemaStepsComponents);

						setState((state) => {
							state.currentFlow.activeFlows = newFlows;
							state.currentFlow.stepsName = newStepsName;
							state.currentFlow.lastStepName =
								newStepsName[newStepsName.length - 1];
							state.currentFlow.activeSchemaStepsComponents =
								newActiveSchemaStepsComponents;
						});
					},
					removeFlow: (flow) => {
						const newFlows = get().currentFlow.activeFlows.filter(
							(activeFlow) => activeFlow !== flow,
						);
						const schemaStepsComponents =
							get().currentFlow.schemaStepsComponents;
						const newActiveSchemaStepsComponents = filterActiveStepsComponents(
							newFlows,
							schemaStepsComponents,
						);
						const newStepsName = Object.keys(newActiveSchemaStepsComponents);

						setState((state) => {
							state.currentFlow.activeFlows = newFlows;
							state.currentFlow.stepsName = newStepsName;
							state.currentFlow.lastStepName =
								newStepsName[newStepsName.length - 1];
							state.currentFlow.activeSchemaStepsComponents =
								newActiveSchemaStepsComponents;
						});
					},
					isFirstStep: () => {
						const currentStep = get().currentFlow.currentStep;
						const firstStep = get().currentFlow.firstStepName;

						return currentStep === firstStep;
					},
					isLastStep: () => {
						const currentStep = get().currentFlow.currentStep;
						const lastStep = get().currentFlow.lastStepName;

						return currentStep === lastStep;
					},
					previousStep: () => {
						const currentStep = get().currentFlow.currentStep;
						const steps = get().currentFlow.stepsName;

						const currentStepIndex = steps.findIndex(
							(currentStepItem) => currentStep === currentStepItem,
						);

						const previousStep = steps[currentStepIndex - 1];

						const newStepComponent =
							get().currentFlow.schemaStepsComponents[previousStep]
								?.component ??
							get().currentFlow.schemaStepsComponents[steps[currentStepIndex]]
								?.component;

						setState((state) => {
							state.currentFlow.currentStep = previousStep;
							state.currentFlow.lastVisitedStep = currentStep;
							state.currentFlow.CurrentStepComponent = newStepComponent;
							state.currentFlow.currentSchemaFormValidation =
								get().currentFlow.schemasFormValidations[previousStep];
						});
					},
					nextStep: () => {
						const currentStep = get().currentFlow.currentStep;
						const steps = get().currentFlow.stepsName;

						const currentStepIndex = steps.findIndex(
							(currentStepItem) => currentStep === currentStepItem,
						);

						const nextStep = steps[currentStepIndex + 1];

						setState((state) => {
							state.currentFlow.currentStep = nextStep;
							state.currentFlow.lastVisitedStep = currentStep;
							state.currentFlow.CurrentStepComponent =
								get().currentFlow.schemaStepsComponents[nextStep].component;
							state.currentFlow.currentSchemaFormValidation =
								get().currentFlow.schemasFormValidations[nextStep];
						});
					},
					goToStep: (stepName: string) => {
						const steps = get().currentFlow.stepsName;

						const step = steps.find((stepItem) => stepName === stepItem);

						if (step) {
							setState((state) => {
								state.currentFlow.currentStep = stepName;
								state.currentFlow.CurrentStepComponent =
									get().currentFlow.schemaStepsComponents[stepName].component;
							});
						}
					},
					updateFormState: (data) => {
						setState((state) => {
							state.formState = { ...get().formState, ...data };
						});
					},
					setIsLoading: (status) => {
						setState((state) => {
							state.currentFlow.isLoading = status;
						});
					},
					setIsValid: (status) => {
						setState((state) => {
							state.currentFlow.isValid = status;
						});
					},
					addFlowId: (id) => {
						setState((state) => {
							state.currentFlow.flowId = id;
						});
					},
					addKBAData: (kbaData, _, missingAttempts) => {
						setState((state) => {
							state.kbaData = {
								missingAttempts: missingAttempts,
								stepQuestionData: kbaData,
							};
						});
					},
					resetKBAData: () => {
						setState((state) => {
							state.kbaData = {
								missingAttempts: 0,
								stepQuestionData: {},
							};
						});
					},
					decrementMissingKBAAttempts: (shouldSetToZero = false) => {
						setState((state) => {
							if (shouldSetToZero) {
								state.kbaData.missingAttempts = 0;
							} else {
								const currentMissingAttemps = state.kbaData.missingAttempts;
								state.kbaData.missingAttempts =
									currentMissingAttemps > 0 ? currentMissingAttemps - 1 : 0;
							}
						});
					},
					lastNavigationDirection: () => {
						const currentStep = get().currentFlow.currentStep;
						const lastVisitedStep = get().currentFlow.lastVisitedStep;
						const steps = get().currentFlow.stepsName;
						const activeSteps = Object.keys(
							get().currentFlow.activeSchemaStepsComponents,
						);

						const direction =
							activeSteps.includes(lastVisitedStep) &&
							steps.indexOf(currentStep) >= steps.indexOf(lastVisitedStep)
								? "next"
								: "previous";

						return direction;
					},
					getFormState: () => {
						return get().formState;
					},
					resetFormState: () => {
						setState((state) => {
							state.formState = {};
						});
					},
					resetFlowStore: () => {
						setState((state) => {
							state.formState = {};
							state.kbaData = {
								missingAttempts: 0,
								stepQuestionData: {},
							};
							state.currentFlow = flowInitialState;
							state.documentResponse = { document: "" };
							state.checkEmailResponse = {
								require_code_check: false,
								email: "",
								isLoading: false,
							};
						});
					},
					addDocumentResponse: (documentResponse) => {
						setState((state) => {
							state.documentResponse = documentResponse;
						});
					},
					addCheckEmailResponse: (checkEmailResponse) => {
						setState((state) => {
							state.checkEmailResponse = checkEmailResponse;
						});
					},
					resetCheckEmailResponse: () => {
						setState((state) => {
							state.checkEmailResponse = {
								require_code_check: false,
								email: "",
								isLoading: false,
							};
						});
					},
					setIsLoadingCheckEmailResponse: (isLoading) => {
						setState((state) => {
							if (!isLoading) state.currentFlow.isLoading = isLoading;

							state.checkEmailResponse.isLoading = isLoading;
						});
					},
					resetDocumentResponse: () => {
						setState((state) => {
							state.documentResponse = { document: "" };
						});
					},
					addFlowResponse: (flows: Flows[]) => {
						setState((state) => {
							state.flowResponse = flows;
						});
					},
					resetFlowResponse: () => {
						setState((state) => {
							state.flowResponse = [""];
						});
					},
				};
			},
			{
				name: "acp-flow-store",
			},
		),
	),
);
